import React from 'react';
import { motion } from 'framer-motion';
import { CalendarCheckIcon, LockIcon, PlugIcon } from 'lucide-react';
import { webappAccents, webappFeatures, webappIntegrations } from '../data/webapplicaties';
const EASE = [0.23, 1, 0.32, 1];
const fadeUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-60px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};

// Illustratieve agenda's: één afspraak via de app, één privé-afspraak die
// alleen als "Bezet" zichtbaar is.
const calendars = [{
  name: 'Agenda collega 1',
  provider: 'Google Agenda'
}, {
  name: 'Agenda collega 2',
  provider: 'Apple Agenda'
}];
export const WebappFeatures: React.FC = () => {
  // Rollen, gebruikers en veiligheid in de middelste rij; taken naast de agenda.
  const [taken, ...rest] = webappFeatures;
  return <section className="w-full bg-white py-20 md:py-24" data-id="element-910" data-mp="37eh3ud7m2j9w">
      <div className="container mx-auto max-w-[1200px] px-4 md:px-6 lg:px-8" data-id="element-911" data-mp="37g1xm0m3py6p">
        <motion.div {...fadeUp} className="mb-12 max-w-2xl" data-id="element-912" data-mp="sa99935d7ugb">
          <h2 className="mb-4 text-3xl font-bold text-[#111827] md:text-4xl" data-id="element-913" data-mp="x7reaig9hpj1">
            Alles wat je team nodig heeft, op één plek
          </h2>
          <p className="text-lg leading-relaxed text-[#6B7280]" data-id="element-914" data-mp="1182wlol5jdrc">
            Elke applicatie bouwen we op maat, maar deze onderdelen komen bijna
            altijd terug. Ze zorgen dat je team echt met de app werkt, en niet
            ernaast.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-915" data-mp="1g5va6b751s2w">
          {/* Agenda-koppeling, uitgelicht */}
          <motion.article {...fadeUp} className="flex flex-col rounded-3xl bg-[#004AC7] p-7 text-white md:col-span-2 md:p-9" data-id="element-916" data-mp="1kyeudr0bzlgo">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-white/15" data-id="element-917" data-mp="1tybh3n35ua93">
              <CalendarCheckIcon className="h-6 w-6" aria-hidden="true" data-id="element-918" data-mp="1yxeceftc923v" />
            </div>
            <h3 className="mb-2 text-2xl font-bold" data-id="element-919" data-mp="3v0izjj9f0w83">
              Koppeling met Google en Apple Agenda
            </h3>
            <p className="mb-8 max-w-xl leading-relaxed text-white/85" data-id="element-920" data-mp="3q1y9udrshuxw">
              Plan vanuit één centraal punt afspraken in de persoonlijke agenda
              van je medewerkers. Zij zien de afspraak direct op hun eigen
              telefoon. Privé-afspraken blijven privé: jij ziet alleen dat
              iemand bezet is, niet wat er staat.
            </p>

            <div className="mt-auto grid grid-cols-1 gap-3 sm:grid-cols-2" aria-hidden="true" data-id="element-921" data-mp="hnha7uftst2g">
              {calendars.map(calendar => <div key={calendar.name} className="rounded-2xl bg-white p-4 text-[#111827]" data-id="element-922" data-mp="rjkasvv14jna">
                  <div className="mb-3 flex items-center justify-between gap-2" data-id="element-923" data-mp="mkzayxjow1zd">
                    <p className="text-sm font-semibold" data-id="element-924" data-mp="miwnol9x8myc">{calendar.name}</p>
                    <p className="text-xs text-[#6B7280]" data-id="element-925" data-mp="2tbfkx71n0g3e">{calendar.provider}</p>
                  </div>
                  <div className="space-y-2" data-id="element-926" data-mp="2odx7b3codcp2">
                    <div className="rounded-lg border-l-4 border-[#004AC7] bg-[#E6F0FF] px-3 py-2" data-id="element-927" data-mp="13tie0b2otren">
                      <p className="text-xs text-[#4B5563]" data-id="element-928" data-mp="18to77volvlyu">09:00</p>
                      <p className="text-sm font-medium" data-id="element-929" data-mp="18uzbdwlyqvqh">Klantbezoek, via de app</p>
                    </div>
                    <div className="flex items-center justify-between rounded-lg border-l-4 border-gray-300 bg-gray-100 px-3 py-2" data-id="element-930" data-mp="z3bpeb86e6we">
                      <div data-id="element-931" data-mp="l2itpn6515rp">
                        <p className="text-xs text-[#4B5563]" data-id="element-932" data-mp="l291dsxmemiu">13:00</p>
                        <p className="text-sm font-medium text-[#4B5563]" data-id="element-933" data-mp="q3omfkzsst18">Bezet</p>
                      </div>
                      <LockIcon className="h-4 w-4 text-[#6B7280]" data-id="element-934" data-mp="b6o4uabbmegh" />
                    </div>
                  </div>
                </div>)}
            </div>
          </motion.article>

          {/* Taken */}
          <FeatureCard feature={taken} accentIndex={1} data-id="element-935" data-mp="25jx7pj9d31pr" />

          {rest.map((feature, index) => <FeatureCard key={feature.title} feature={feature} accentIndex={index + 2} data-id="element-936" data-mp="2ah75bm6wmjoo" data-mpi={index} />)}

          {/* Koppelingen */}
          <motion.article {...fadeUp} className="rounded-3xl border border-gray-200 bg-white p-7 md:col-span-2 md:p-9 lg:col-span-3" data-id="element-937" data-mp="afg05jkp63p9">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center" data-id="element-938" data-mp="1fkhyusq58fq">
              <div className="lg:col-span-5" data-id="element-939" data-mp="1hdtanea8gu2">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-sky-100 text-sky-700" data-id="element-940" data-mp="3sksom917dome">
                  <PlugIcon className="h-6 w-6" aria-hidden="true" data-id="element-941" data-mp="3noc03dq3d88s" />
                </div>
                <h3 className="mb-2 text-2xl font-bold text-[#111827]" data-id="element-942" data-mp="3iv94z15o1qqh">
                  Koppelingen met je bekende software
                </h3>
                <p className="leading-relaxed text-[#6B7280]" data-id="element-943" data-mp="39vcm5g5yjm3c">
                  Je boekhouding, agenda, mail en betalingen praten met de app.
                  Gegevens voer je één keer in en lopen daarna vanzelf door. Dit
                  is een greep uit pakketten met een koppeling. Wat er precies
                  kan, zoeken we per pakket voor je uit.
                </p>
              </div>
              <ul className="flex flex-wrap gap-2.5 lg:col-span-7" data-id="element-944" data-mp="2uscjyl9z2r6n">
                {webappIntegrations.map((name, index) => <li key={name} className={`rounded-full px-4 py-2 text-sm font-medium ${webappAccents[index % webappAccents.length].chip}`} data-id="element-945" data-mp="2zrp591l73q74" data-mpi={index}>
                    {name}
                  </li>)}
              </ul>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
};
interface FeatureCardProps {
  feature: (typeof webappFeatures)[number];
  accentIndex: number;
}
const FeatureCard: React.FC<FeatureCardProps> = ({
  feature,
  accentIndex
}) => {
  const Icon = feature.icon;
  const accent = webappAccents[accentIndex % webappAccents.length];
  return <motion.article {...fadeUp} className={`flex flex-col rounded-3xl p-7 ${accent.tint}`} data-id="element-946" data-mp="2lthe5aaubj3v">
      <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-xl ${accent.chip}`} data-id="element-947" data-mp="1oa673nmh2dff">
        <Icon className="h-6 w-6" aria-hidden="true" data-id="element-948" data-mp="1t9918d0wyz8m" />
      </div>
      <h3 className="mb-2 text-xl font-bold text-[#111827]" data-id="element-949" data-mp="1l9csviuevn1q">{feature.title}</h3>
      <p className="leading-relaxed text-[#4B5563]" data-id="element-950" data-mp="1gccm15pqmg9k">{feature.body}</p>
    </motion.article>;
};